import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import {
  GetExternalLeadResponseStatus,
  getExternalLeadService,
  LookingForHelpDto,
  LookingForHelpExtensionDto,
} from '@hc-frontend/services';
import { find } from 'ramda';

import type { GetAnswersRepository, PartialSurveyAnswers } from '../../domain';
import { LookingForHelp, LookingForHelpExtension } from '../../domain';

export function getAnswersAdapterFactory({
  baseUrl,
}: {
  baseUrl: string;
}): GetAnswersRepository {
  return {
    getAnswers: async (params) => {
      if (!params.sessionId && !params.userId) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToGetAnswers',
            },
          ],
        });
      }

      const response = await getExternalLeadService({ baseUrl })({
        ...params,
        leadStatusId: GetExternalLeadResponseStatus.PROSPECT.toString(),
      });

      if (response.status !== 200 || response.body === undefined) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToGetAnswers',
              rootCause: new Error(
                `Session ${params.sessionId} could not be retreived`,
              ),
            },
          ],
        });
      }

      const askForZipCode = find(
        (val) => val.name === 'askForZipCode',
        response.body.journeyAttributes,
      );

      const isPhoenix = find(
        (val) => val.name === 'is_phoenix',
        response.body.journeyAttributes,
      );

      const isOep = find(
        (val) => val.name === 'is-oep',
        response.body.journeyAttributes,
      );

      const lookingForHelp = find(
        (val) => val.name === 'looking_for_help',
        response.body.journeyAttributes,
      );

      const lookingForHelpExtension = find(
        (val) => val.name === 'looking_for_help_extension',
        response.body.journeyAttributes,
      );

      const lookingForHelpMap: Record<LookingForHelpDto, LookingForHelp> = {
        [LookingForHelpDto.Enum.compare_plans]:
          LookingForHelp.Enum.COMPARE_PLANS,
        [LookingForHelpDto.Enum.lower_costs]: LookingForHelp.Enum.LOWER_COSTS,
        [LookingForHelpDto.Enum.doctor_coverage]:
          LookingForHelp.Enum.DOCTOR_COVERAGE,
        [LookingForHelpDto.Enum.learn]: LookingForHelp.Enum.LEARN,
        [LookingForHelpDto.Enum.existing_plan]:
          LookingForHelp.Enum.EXISTING_PLAN,
        [LookingForHelpDto.Enum.something_else]:
          LookingForHelp.Enum.SOMETHING_ELSE,
      };

      const lookingForHelpExtensionMap: Record<
        LookingForHelpExtensionDto,
        LookingForHelpExtension
      > = {
        [LookingForHelpExtensionDto.Enum.specific_situation]:
          LookingForHelpExtension.Enum.SPECIFIC_SITUATION,
        [LookingForHelpExtensionDto.Enum.helping_someone_else]:
          LookingForHelpExtension.Enum.HELPING_SOMEONE_ELSE,
        [LookingForHelpExtensionDto.Enum.improve_coverage]:
          LookingForHelpExtension.Enum.IMPROVE_COVERAGE,
        [LookingForHelpExtensionDto.Enum.dental_vision_supplemental]:
          LookingForHelpExtension.Enum.DENTAL_VISION_SUPPLEMENTAL,
      };

      const answers: PartialSurveyAnswers = {
        ASK_FOR_ZIP_CODE:
          askForZipCode?.value === 'false'
            ? false
            : Boolean(askForZipCode?.value),
        IS_PHOENIX:
          isPhoenix?.value === 'false' ? false : Boolean(isPhoenix?.value),
        IS_OEP: isOep?.value === 'is-oep-yes',
        LOOKING_FOR_HELP: lookingForHelp?.value
          ? lookingForHelpMap[lookingForHelp.value as LookingForHelpDto]
          : undefined,
        LOOKING_FOR_HELP_EXTENSION: lookingForHelpExtension?.value
          ? lookingForHelpExtensionMap[
              lookingForHelpExtension.value as LookingForHelpExtensionDto
            ]
          : undefined,
      };

      return createSuccessOperationResult(answers);
    },
  };
}
